import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT } from "@/constants";
import { fmtN, PeriodTabs } from "./common";

const ACTION_LABELS = {
  ajouter: "Action : ajouter", completer: "Action : compléter", remplacer: "Action : remplacer",
  corriger: "Action : corriger", modifier: "Action : modifier", verifier: "À vérifier", rien: "Ne rien faire",
};
const CONF_LABELS = { "élevée": "Confiance élevée", "moyenne": "Confiance moyenne", "estimée": "Estimation" };
const STATUS_LABELS = { a_faire: "À faire", en_cours: "En cours", effectue: "Effectué" };
const STATUS_CLS = {
  a_faire: "bg-slate-200 text-slate-600",
  en_cours: "bg-sky-100 text-sky-700",
  effectue: "bg-emerald-100 text-emerald-700",
};
const shortUrl = (u) => (u || "").replace("https://www.photobooth54.fr", "") || "/";

function CopyBtn({ text, label, testid }) {
  return (
    <button data-testid={testid} onClick={async () => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copié — collez-le dans Webador`);
      } catch {
        toast.error("Copie impossible — sélectionnez le texte manuellement");
      }
    }} className="shrink-0 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs font-bold hover:bg-slate-800">
      Copier
    </button>
  );
}

function PagesVisibles({ p, index }) {
  if (!p.pages?.length) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid={`seo-priority-nopage-${index}`}>
        <p className="text-xs font-bold text-amber-900">Page non identifiée — données insuffisantes</p>
        <p className="text-xs text-amber-800">{p.next_step}</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1.5" data-testid={`seo-priority-visible-${index}`}>
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Page(s) actuellement visible(s) dans Google — ce que Google montre aujourd'hui, pas forcément la meilleure page à optimiser</p>
      {p.pages.map((pg) => (
        <p key={pg.page} className="text-[11px] font-mono tabular-nums text-slate-500 break-all">
          {shortUrl(pg.page)} — {fmtN(pg.impressions)} imp. · {fmtN(pg.clicks)} clic{pg.clicks > 1 ? "s" : ""} · CTR {pg.ctr} % · pos. {pg.position}
        </p>
      ))}
      {p.page_advice && <p className="text-xs text-slate-600 italic">{p.page_advice}</p>}
    </div>
  );
}

function TargetBlock({ p, index }) {
  if (!p.page) return null;
  return (
    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-3 space-y-1.5" data-testid={`seo-priority-target-${index}`}>
      <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Page cible recommandée — c'est sur celle-ci qu'on travaille</p>
      <p className="text-sm font-semibold break-all">{p.page}</p>
      {p.target_evidence?.length > 0 && (
        <p className="text-xs text-slate-600" data-testid={`seo-priority-evidence-${index}`}>Preuves dans le contenu réel de la page : {p.target_evidence.join(" · ")}</p>
      )}
      {p.target_reason && <p className="text-xs text-slate-600">{p.target_reason}</p>}
      {p.indexation_note && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`seo-priority-indexation-${index}`}>{p.indexation_note}</p>
      )}
    </div>
  );
}

function LinkBlock({ p, index }) {
  const s = p.internal_links?.suggestion;
  const linked = p.internal_links?.linked_from || [];
  if (!s && !linked.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1.5" data-testid={`seo-priority-links-${index}`}>
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Liens internes vers la page cible (constatés sur le site)</p>
      {linked.length > 0 && <p className="text-xs text-slate-600">Déjà liée depuis : {linked.map(shortUrl).join(", ")}</p>}
      {s && (
        <div className="text-xs text-slate-700 space-y-1">
          <p>Lien utile à ajouter : depuis <strong>{shortUrl(s.from)}</strong> vers <strong>{shortUrl(s.to)}</strong></p>
          <div className="flex items-center gap-2 flex-wrap">
            <p>Texte de lien proposé : <strong>« {s.anchor} »</strong></p>
            <CopyBtn testid={`seo-priority-copy-anchor-${index}`} text={s.anchor} label="Texte de lien" />
          </div>
          <p className="text-slate-500">Emplacement : {s.placement}.</p>
        </div>
      )}
    </div>
  );
}

function Diagnostic({ d, index }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1.5 text-xs" data-testid={`seo-priority-diagnostic-${index}`}>
      <p><span className="font-bold text-slate-600">Faits mesurés :</span> {d.facts}</p>
      <p><span className="font-bold text-slate-600">Hypothèse :</span> {d.hypothesis}</p>
      <p><span className="font-bold text-slate-600">Pourquoi prioritaire :</span> {d.why}</p>
    </div>
  );
}

function BeforeAfter({ p, index, onCurrent }) {
  const [cur, setCur] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paste, setPaste] = useState({ title: "", meta: "" });
  const parts = p.suggestion_parts;
  if (!parts) return null;
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/seo/page-check", { params: { url: p.page } });
      setCur(r.data);
      if (r.data.fetched) onCurrent({ title: r.data.title.value, meta: r.data.meta.value });
    } catch (e) {
      setCur({ fetched: false, error: e?.response?.data?.detail || "Lecture impossible — collez les textes actuels manuellement." });
    }
    setLoading(false);
  };
  const setPasteField = (k, v) => {
    const next = { ...paste, [k]: v };
    setPaste(next);
    onCurrent(next);
  };
  return (
    <div className="space-y-2" data-testid={`seo-priority-beforeafter-${index}`}>
      {!cur && (
        <button data-testid={`seo-priority-load-current-${index}`} onClick={load} disabled={loading}
          className="rounded-lg border border-sky-300 text-sky-700 px-3 py-2 text-xs font-bold disabled:opacity-50">
          {loading ? "Lecture du site…" : "Lire les textes actuels sur le site"}
        </button>
      )}
      {cur && !cur.fetched && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid={`seo-priority-fetchfail-${index}`}>
          <p className="text-xs font-bold text-amber-900">Lecture automatique impossible</p>
          <p className="text-xs text-amber-800">{cur.error} Vous pouvez coller ci-dessous les textes actuels (dans Webador : Paramètres → SEO).</p>
          <input data-testid={`seo-priority-paste-title-${index}`} value={paste.title}
            onChange={(e) => setPasteField("title", e.target.value)}
            placeholder="Titre SEO actuel" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white" />
          <textarea data-testid={`seo-priority-paste-meta-${index}`} value={paste.meta} rows={2}
            onChange={(e) => setPasteField("meta", e.target.value)}
            placeholder="Méta-description actuelle" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white" />
        </div>
      )}
      {cur?.fetched && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1 text-xs" data-testid={`seo-priority-current-${index}`}>
          <p className="font-bold text-slate-500 uppercase tracking-wider">Textes actuellement en ligne</p>
          <p><strong>Titre :</strong> {cur.title.empty
            ? <span className="text-amber-700">champ réellement vide sur le site</span>
            : (cur.title.value || <span className="text-amber-700">non détecté dans la page</span>)}</p>
          <p><strong>Méta-description :</strong> {cur.meta.empty
            ? <span className="text-amber-700">champ réellement vide sur le site</span>
            : (cur.meta.value || <span className="text-amber-700">non détectée dans la page</span>)}</p>
          <p><strong>H1 :</strong> {cur.h1.empty ? "champ vide" : (cur.h1.value || "non détecté dans la page")}</p>
        </div>
      )}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid={`seo-priority-proposed-${index}`}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Textes proposés — prêts à copier</p>
          {parts.ai && <span className="text-[9px] font-bold bg-emerald-600 text-white rounded-full px-2 py-0.5" data-testid={`seo-priority-ai-${index}`}>Rédigés par l'IA</span>}
        </div>
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm"><strong>Titre :</strong> {parts.title}</p>
          <CopyBtn testid={`seo-priority-copy-title-${index}`} text={parts.title} label="Titre" />
        </div>
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm"><strong>Méta :</strong> {parts.meta}</p>
          <CopyBtn testid={`seo-priority-copy-meta-${index}`} text={parts.meta} label="Méta-description" />
        </div>
        {parts.h1 && (
          <div className="flex items-start justify-between gap-2" data-testid={`seo-priority-h1-${index}`}>
            <p className="text-sm"><strong>H1 :</strong> {parts.h1}</p>
            <CopyBtn testid={`seo-priority-copy-h1-${index}`} text={parts.h1} label="H1" />
          </div>
        )}
        {parts.paragraph && (
          <div className="flex items-start justify-between gap-2" data-testid={`seo-priority-paragraph-${index}`}>
            <p className="text-sm"><strong>Paragraphe à ajouter :</strong> {parts.paragraph}</p>
            <CopyBtn testid={`seo-priority-copy-paragraph-${index}`} text={parts.paragraph} label="Paragraphe" />
          </div>
        )}
        {parts.alt && (
          <div className="flex items-start justify-between gap-2" data-testid={`seo-priority-alt-${index}`}>
            <p className="text-sm"><strong>Texte ALT (photo) :</strong> {parts.alt}</p>
            <CopyBtn testid={`seo-priority-copy-alt-${index}`} text={parts.alt} label="Texte ALT" />
          </div>
        )}
        <p className="text-xs text-slate-500 italic">{parts.explanation}</p>
      </div>
    </div>
  );
}

function WebadorGuide({ p, index }) {
  if (!p.page || (!p.suggestion_parts && !p.internal_links?.suggestion)) return null;
  const parts = p.suggestion_parts;
  const link = p.internal_links?.suggestion;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid={`seo-priority-guide-${index}`}>
      <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Guide Webador pas à pas</p>
      {parts && (
        <div className="space-y-1.5">
          <p className="text-xs font-bold text-slate-700">Étape A — Page {shortUrl(p.page)} <span className="font-normal text-slate-400">(page cible)</span></p>
          <ol className="text-xs text-slate-600 space-y-1.5 list-decimal pl-4">
            <li>Ouvrez votre éditeur Webador (webador.fr → « Modifier mon site »).</li>
            <li>Allez dans <strong>Paramètres → SEO</strong>, onglet « Pages », puis cliquez la page <strong>{shortUrl(p.page)}</strong>.
              Chemin alternatif documenté par Webador : onglet <strong>Pages</strong> → roue dentée à côté de la page → section « SEO ».</li>
            <li>Dans le champ <strong>« Titre de page »</strong>, collez le titre proposé (bouton « Copier » ci-dessus).</li>
            <li>Dans le champ <strong>« Description de page »</strong>, collez la méta-description proposée.</li>
            <li><strong>Enregistrez</strong>, puis cliquez <strong>« Publier »</strong>.</li>
          </ol>
        </div>
      )}
      {link && (
        <div className="space-y-1.5" data-testid={`seo-priority-guide-link-${index}`}>
          <p className="text-xs font-bold text-slate-700">Étape {parts ? "B" : "A"} — Page {shortUrl(link.from)} <span className="font-normal text-slate-400">(lien interne vers la page cible)</span></p>
          <ol className="text-xs text-slate-600 space-y-1.5 list-decimal pl-4">
            <li>Dans l'éditeur Webador, ouvrez la page <strong>{shortUrl(link.from)}</strong>.</li>
            <li>{link.placement} : ajoutez le texte de lien <strong>« {link.anchor} »</strong> (bouton « Copier » dans l'encart Liens internes).</li>
            <li>Sélectionnez ce texte et transformez-le en lien vers <strong>{link.to}</strong> — dans Webador, sélectionnez le texte puis utilisez l'icône de lien de la barre d'édition. Ce chemin exact n'est pas confirmé par la documentation officielle : à vérifier dans l'éditeur.</li>
            <li><strong>Enregistrez</strong>, puis <strong>« Publier »</strong>.</li>
          </ol>
        </div>
      )}
      <p className="text-xs text-slate-600">Terminez en revenant ici : marquez l'action « Effectuée », puis dans l'onglet Historique & bilans cliquez « Vérifier sur le site ».</p>
      <p className="text-[11px] text-slate-400">Chemins issus de l'aide officielle Webador (« Paramètres SEO pour vos pages » et « Ajouter une page »).
        Le H1 n'a pas de champ dédié documenté : il correspond au titre affiché en haut de votre page dans l'éditeur — chemin non confirmé, à vérifier dans Webador.</p>
    </div>
  );
}

function PriorityCard({ p, index, onStatus }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const status = p.status || "a_faire";
  return (
    <div className={`rounded-2xl border-2 p-4 space-y-2.5 ${status === "effectue" ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/60"}`}
      data-testid={`seo-priority-${index}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <p className="font-heading font-extrabold text-sm">🎯 PRIORITÉ {index + 1} — {p.title}</p>
        <div className="flex gap-1.5 shrink-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.source === "confirmée" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
            data-testid={`seo-priority-source-${index}`}>
            {p.source === "confirmée" ? "Données Google" : "Estimation"}
          </span>
          {p.action_type && ACTION_LABELS[p.action_type] && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700" data-testid={`seo-priority-actiontype-${index}`}>
              {ACTION_LABELS[p.action_type]}
            </span>
          )}
          {p.validation?.status === "verifier" && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-800" data-testid={`seo-priority-verify-${index}`}>
              Vérification nécessaire
            </span>
          )}
          {p.confidence && CONF_LABELS[p.confidence] && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.confidence === "élevée" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
              data-testid={`seo-priority-confidence-${index}`}>
              {CONF_LABELS[p.confidence]}
            </span>
          )}
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[status]}`}
            data-testid={`seo-priority-status-${index}`}>
            {STATUS_LABELS[status]}
          </span>
        </div>
      </div>
      {p.queries?.length > 0 && (
        <div className="flex gap-1.5 flex-wrap" data-testid={`seo-priority-queries-${index}`}>
          {p.queries.map((q) => (
            <span key={q.query} className="text-[11px] font-semibold bg-white border border-slate-200 rounded-full px-2 py-0.5 font-mono tabular-nums">
              « {q.query} » : {fmtN(q.impressions)} imp. · {q.clicks} clic{q.clicks > 1 ? "s" : ""} · pos. {q.position}
            </span>
          ))}
        </div>
      )}
      <p className="text-sm text-slate-700">{p.problem}</p>
      {p.validation?.warnings?.length > 0 && (
        <p className="text-xs text-amber-800 italic" data-testid={`seo-priority-warnings-${index}`}>⚠ {p.validation.warnings.join(" · ")}</p>
      )}
      {p.suggestion_parts?.action_justification && (
        <p className="text-xs text-slate-500" data-testid={`seo-priority-justif-${index}`}>{p.suggestion_parts.action_justification}</p>
      )}
      {p.suggestion_parts?.risk && (
        <p className="text-xs text-rose-700" data-testid={`seo-priority-risk-${index}`}>Risque : {p.suggestion_parts.risk}</p>
      )}
      {p.suggestion_parts?.current_text && (
        <p className="text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`seo-priority-currenttext-${index}`}>
          <strong>Texte actuel à modifier :</strong> « {p.suggestion_parts.current_text} »
        </p>
      )}
      {p.kind === "ctr" && <PagesVisibles p={p} index={index} />}
      {p.kind !== "local" && <TargetBlock p={p} index={index} />}
      {p.kind === "ctr" && <LinkBlock p={p} index={index} />}
      {p.diagnostic && <Diagnostic d={p.diagnostic} index={index} />}
      <p className="text-sm font-semibold text-slate-800">{p.action}</p>
      {open && p.page && <BeforeAfter p={p} index={index} onCurrent={setCurrent} />}
      {open && p.suggestion && !p.suggestion_parts && (
        <pre className="text-xs bg-white border border-emerald-200 rounded-xl p-3 whitespace-pre-wrap font-sans" data-testid={`seo-priority-suggestion-${index}`}>{p.suggestion}</pre>
      )}
      {open && <WebadorGuide p={p} index={index} />}
      <div className="flex gap-2 flex-wrap pt-1">
        <button data-testid={`seo-priority-detail-${index}`} onClick={() => setOpen(!open)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-white">
          {open ? "Masquer" : "Voir le détail"}
        </button>
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          <button key={k} data-testid={`seo-priority-setstatus-${k}-${index}`}
            onClick={() => onStatus(p, k, current)} disabled={status === k}
            className={`rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-40 ${k === "effectue" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SeoPriorities() {
  const [period, setPeriod] = useState("28");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setError(false);
    api.get("/seo/priorities", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => { setData({ items: [] }); setError(true); });
  }, [period]);
  useEffect(() => { setData(null); load(); }, [load]);
  const setStatus = async (p, status, current) => {
    try {
      await api.post("/seo/priorities/state", {
        key: p.key, status, title: p.title, page: p.page, commune: p.commune,
        source: p.source, suggestion: p.suggestion, data: p.data,
        texts_before: current || undefined,
        texts_after: p.suggestion_parts ? { title: p.suggestion_parts.title, meta: p.suggestion_parts.meta } : undefined,
      });
      toast.success(status === "effectue"
        ? "Action enregistrée dans l'historique — vérifiez la publication sur le site, puis suivez l'évolution sur une période comparable"
        : `Statut « ${STATUS_LABELS[status]} » enregistré`);
      load();
    } catch {
      toast.error("Enregistrement impossible — réessayez");
    }
  };
  if (!data) return <p className="text-sm text-slate-400">Chargement…</p>;
  const items = data.items || [];
  return (
    <div className="space-y-3" data-testid="seo-priorities">
      <div className="text-xs text-slate-500 space-y-2">
        <p className="font-heading font-extrabold text-base text-slate-900">🎯 Mes 3 priorités SEO</p>
        <p className="rounded-lg bg-sky-50 border border-sky-100 px-3 py-2" data-testid="seo-priorities-help">
          <strong>Comment utiliser cette page :</strong> laissez la période sur <strong>28 jours</strong> (la plus fiable),
          ouvrez la priorité n°1, copiez les textes proposés (boutons « Copier »), collez-les dans Webador en suivant le guide,
          puis marquez-la « Effectuée ». Passez ensuite à la suivante — une action terminée ne vous sera plus jamais reproposée.{" "}
          <a href="/notice-assistant-seo.pdf" target="_blank" rel="noreferrer" data-testid="seo-notice-link" className="font-semibold text-sky-700 underline underline-offset-2">
            Télécharger la notice complète (PDF)
          </a>
        </p>
        <p>Chaque priorité identifie la page réelle à modifier (données Google Search Console), compare l'existant aux textes proposés et vous guide dans Webador.</p>
        <PeriodTabs value={period} onChange={setPeriod} />
        {data.start && data.end && (
          <p data-testid="seo-priorities-period">Période analysée : <strong>du {data.start} au {data.end}</strong>
            {data.synced_at ? ` — synchronisée le ${fmtDT(data.synced_at)}` : ""}
            {data.generated_at ? ` · priorités calculées le ${fmtDT(data.generated_at)}` : ""}</p>
        )}
      </div>
      {error && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-4 space-y-2" data-testid="seo-priorities-error">
          <p className="text-sm font-semibold text-rose-800">Chargement des priorités impossible — le reste de l'application n'est pas affecté.</p>
          <button data-testid="seo-priorities-retry" onClick={load}
            className="rounded-lg bg-rose-600 text-white px-4 py-2 text-xs font-bold">Réessayer</button>
        </div>
      )}
      {!error && items.length === 0 && (
        <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-4" data-testid="seo-priorities-empty">
          Aucune priorité pour l'instant : connectez Search Console et/ou calculez les communes locales (onglet Paramètres).
        </p>
      )}
      {items.map((p, i) => <PriorityCard key={p.key || i} p={p} index={i} onStatus={setStatus} />)}
    </div>
  );
}
